import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { useAppContext } from '../AppContext';
import { useDynamicStyle } from '../hooks/useDynamicStyle';
import { PaletteIcon, DiamondIcon, SparklesIcon, CheckCircleIcon, LockIcon, RotateCcwIcon } from '../components/icons';
import ColorAnalysisModal from '../components/ColorAnalysisModal';

const ColorStudioPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
    const { t } = useTranslation();
    const { userProfile, currentUserId, updateUserProfile, savedOutfits } = useAppContext();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const result = userProfile?.colorAnalysis;

    // Access control: Only premium and advanced users
    const isGuest = currentUserId?.includes('guest') ?? true;
    const hasAccess = userProfile?.membershipLevel === 'premium' || userProfile?.membershipLevel === 'advanced';
    const isLocked = isGuest || userProfile?.membershipLevel === 'basic';

    useSEO(
        t('colorStudio_seo_title'),
        t('colorStudio_seo_desc'),
        t('colorStudio_seo_keywords').split(',').map(k => k.trim())
    );

    const ColorSwatch: React.FC<{ color: string; label?: string }> = ({ color, label }) => {
        const styleRef = useDynamicStyle<HTMLDivElement>({ '--swatch-color': color });

        return (
            <div className="flex flex-col items-center gap-2 group">
                <div
                    ref={styleRef}
                    className="w-16 h-16 rounded-full border-2 border-white dark:border-gray-800 shadow-md transform transition-transform group-hover:scale-105 bg-[var(--swatch-color)]"
                    title={color}
                />
                {label && <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider">{label}</span>}
            </div>
        );
    };

    const handleStartNewAnalysis = () => {
        if (result) {
            if (window.confirm(t('colorAnalysis_confirm_new'))) {
                setIsModalOpen(true);
            }
        } else {
            setIsModalOpen(true);
        }
    };

    return (
        <div className={`${embedded ? 'w-full' : 'min-h-screen bg-white dark:bg-gray-900 pt-20 flex flex-col'}`}>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${embedded ? '' : 'max-w-4xl mx-auto px-4'} w-full flex-grow`}            >
                <div className={`${embedded ? 'text-left' : 'text-center py-8'}`}>
                    {!embedded && (
                        <>
                            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-white mb-4">
                                {t('colorStudio_title')}
                            </h1>
                            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                                {t('colorStudio_subtitle')}
                            </p>
                        </>
                    )}

                    {/* Access Control - Show locked message for basic/guest users */}
                    {isLocked && (
                        <div className="p-12 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl border-2 border-indigo-200 dark:border-purple-700 text-center mb-8">
                            <LockIcon className="w-20 h-20 text-indigo-500 dark:text-purple-400 mx-auto mb-6" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                {isGuest ? t('feature_locked_guest') : t('feature_locked_premium_generic')}
                            </h3>
                            <p className="text-gray-700 dark:text-gray-300 mb-6 max-w-md mx-auto">
                                {t('colorStudio_premium_required')}
                            </p>
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={() => window.location.href = '/profile'}
                                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors"
                                >
                                    {t('upgrade_membership')}
                                </button>
                                <button
                                    onClick={() => window.location.href = '/services'}
                                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-bold rounded-lg transition-colors"
                                >
                                    {t('view_plans')}
                                </button>
                            </div>
                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('unlock_report_desc')}</p>
                                <button
                                    onClick={() => alert('Mock Payment: Report Unlocked!')}
                                    className="px-6 py-3 bg-white dark:bg-gray-800 border-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 font-bold rounded-lg transition-colors inline-flex items-center gap-2"
                                >
                                    <SparklesIcon className="w-5 h-5" />
                                    {t('unlock_report_button')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Content - Only show if user has access */}
                    {!isLocked && (
                        <>
                            {!result ? (
                                <div className="p-12 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 text-center">
                                    <PaletteIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('colorAnalysis_no_result_title')}</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                        {t('colorAnalysis_no_result_desc')}
                                    </p>
                                    <button
                                        onClick={handleStartNewAnalysis}
                                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                                    >
                                        {t('colorAnalysis_start_button')}
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-8 animate-fade-in text-left">
                                    {/* Season Result */}
                                    <div className="text-center p-8 bg-indigo-50 dark:bg-indigo-950/40 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 relative overflow-hidden">
                                        <div className="relative z-10">
                                            <div className="flex items-center justify-center gap-4 mb-2">
                                                <span className="text-sm font-bold uppercase tracking-widest text-indigo-700 dark:text-indigo-400 block">{t('colorAnalysis_season_label')}</span>
                                                <button
                                                    onClick={handleStartNewAnalysis}
                                                    className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                    title={t('colorAnalysis_analyze_new')}
                                                >
                                                    <RotateCcwIcon className="w-4 h-4 text-indigo-600" />
                                                </button>
                                            </div>
                                            <h3 className="text-5xl font-serif font-bold text-gray-900 dark:text-white mb-4">{result.season}</h3>

                                            <div className="flex flex-wrap justify-center gap-3 mt-6">
                                                {Object.entries(result.characteristics).map(([key, value]) => {
                                                    const characteristicLabelMap: Record<string, string> = {
                                                        skinTone: t('colorAnalysis_characteristic_skinTone'),
                                                        hairColor: t('colorAnalysis_characteristic_hairColor'),
                                                        eyeColor: t('colorAnalysis_characteristic_eyeColor'),
                                                        faceShape: t('colorAnalysis_characteristic_faceShape'),
                                                        bodySize: t('colorAnalysis_characteristic_bodySize'),
                                                    };
                                                    return value && typeof value === 'string' && (
                                                        <span key={key} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200 capitalize">
                                                            <span className="opacity-60 mr-1">{characteristicLabelMap[key] || key.replace(/([A-Z])/g, ' $1').trim()}:</span> {value}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        {/* Decorative splotches */}
                                        <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-200/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
                                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-200/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
                                    </div>

                                    {/* Power Palette */}
                                    <div className="bg-white dark:bg-gray-800/50 p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <h4 className="text-2xl font-serif font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                                            <DiamondIcon className="w-7 h-7 text-indigo-500" /> {t('colorAnalysis_powerColors')}
                                        </h4>
                                        <div className="flex flex-wrap items-center justify-around gap-8 p-8 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/20 dark:to-purple-950/20 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30">
                                            {result.palette.powerColors.map((color, i) => (
                                                <ColorSwatch key={i} color={color} />
                                            ))}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center italic">
                                            {t('colorStudio_powerColors_desc')}
                                        </p>
                                    </div>

                                    {/* Neutrals & Avoid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700">
                                            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4 uppercase flex items-center gap-2">
                                                <div className="w-2 h-8 bg-gray-400 rounded-full"></div>
                                                {t('colorAnalysis_neutrals')}
                                            </h4>
                                            <div className="flex flex-wrap gap-4">
                                                {result.palette.neutrals.map((color, i) => (
                                                    <ColorSwatch key={i} color={color} />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700">
                                            <h4 className="text-lg font-bold text-red-700 dark:text-red-400 mb-4 uppercase flex items-center gap-2">
                                                <div className="w-2 h-8 bg-red-400 rounded-full"></div>
                                                {t('colorAnalysis_avoid')}
                                            </h4>
                                            <div className="flex flex-wrap gap-4">
                                                {result.palette.avoidColors.map((color, i) => (
                                                    <ColorSwatch key={i} color={color} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Advice Section */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-hover hover:shadow-md">
                                            <h4 className="font-serif text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                                <SparklesIcon className="w-6 h-6 text-yellow-500" /> {t('colorAnalysis_advice_hair_makeup')}
                                            </h4>
                                            <div className="space-y-4">
                                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                    <strong className="block text-sm uppercase text-gray-500 dark:text-gray-400 mb-1">{t('colorAnalysis_label_hair')}</strong>
                                                    <p className="text-gray-800 dark:text-gray-200">{result.advice.hair}</p>
                                                </div>
                                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                    <strong className="block text-sm uppercase text-gray-500 dark:text-gray-400 mb-1">{t('colorAnalysis_label_makeup')}</strong>
                                                    <p className="text-gray-800 dark:text-gray-200">{result.advice.makeup}</p>
                                                </div>
                                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                    <strong className="block text-sm uppercase text-gray-500 dark:text-gray-400 mb-1">{t('colorAnalysis_label_jewelry')}</strong>
                                                    <p className="text-gray-800 dark:text-gray-200">{result.advice.jewelry}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-hover hover:shadow-md">
                                            <h4 className="font-serif text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                                <CheckCircleIcon className="w-6 h-6 text-green-500" /> {t('colorAnalysis_advice_clothing')}
                                            </h4>
                                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg h-full">
                                                <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-line">{result.advice.clothing}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </motion.div>
            <ColorAnalysisModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                result={result || null}
                savedOutfits={savedOutfits}
                onSaveToProfile={(attrs) => updateUserProfile(prev => ({ ...prev, ...attrs, colorAnalysis: result || prev.colorAnalysis }))}
            />
            {!embedded && <Footer onNavigate={() => { }} />}
        </div>
    );
};

export default ColorStudioPage;
